import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Lock, User, Building2, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [companyCode, setCompanyCode] = useState('');
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { register } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Fetch available companies
        const fetchCompanies = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/companies/public`);
                setCompanies(response.data);
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };
        fetchCompanies();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('As passwords não coincidem');
            return;
        }

        if (password.length < 6) {
            setError('A password deve ter pelo menos 6 caracteres');
            return;
        }

        setLoading(true);

        try {
            await register(email, password, name, companyCode);
            toast.success('Conta criada! Aguarda aprovação do administrador.');
            navigate('/pending-approval');
        } catch (err) {
            const message = err.response?.data?.detail || 'Erro ao criar conta';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div
                className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
                style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1726866672851-5b99c837603c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMGluZHVzdHJpYWwlMjByb29mfGVufDB8fHx8MTc2ODE1NTE1OHww&ixlib=rb-4.1.0&q=85')`
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-emerald-900/70" />
                <div className="relative z-10 flex flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-3">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_powermonitor-5/artifacts/rtabpwt5_Sem%20Fundo.png" 
                            alt="MedEletrões Logo" 
                            className="w-24 h-24 object-contain"
                        />
                        <div>
                            <h1 className="text-2xl font-bold font-heading">MedEletrões</h1>
                            <p className="text-sm text-emerald-200">Gestão de Energia</p>
                        </div>
                    </div>
                    
                    <div className="space-y-6">
                        <h2 className="text-4xl font-bold font-heading leading-tight">
                            Comece a poupar<br />
                            energia hoje
                        </h2>
                        <p className="text-lg text-slate-300 max-w-md">
                            Crie a sua conta e comece a monitorizar os seus dispositivos Shelly.
                        </p>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <span className="text-emerald-400 font-bold">✓</span>
                                </div>
                                <span className="text-slate-300">Dashboard em tempo real</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <span className="text-emerald-400 font-bold">✓</span>
                                </div>
                                <span className="text-slate-300">Alertas personalizados</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <span className="text-emerald-400 font-bold">✓</span>
                                </div>
                                <span className="text-slate-300">Exportação de relatórios</span>
                            </div>
                        </div>
                    </div>
                    
                    <p className="text-sm text-slate-400">
                        © 2024 MedEletrões. Desenvolvido por <a href="https://www.inforexe.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">Infor.Exe</a>
                    </p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_powermonitor-5/artifacts/rtabpwt5_Sem%20Fundo.png" 
                            alt="MedEletrões Logo" 
                            className="w-24 h-24 object-contain"
                        />
                        <div>
                            <h1 className="text-2xl font-bold font-heading text-slate-900">MedEletrões</h1>
                            <p className="text-sm text-slate-500">Gestão de Energia</p>
                        </div>
                    </div>

                    <Card className="border-0 shadow-xl">
                        <CardHeader className="space-y-1 pb-6">
                            <CardTitle className="text-2xl font-bold font-heading">Criar Conta</CardTitle>
                            <CardDescription>
                                Preencha os dados para criar a sua conta
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        <p>{error}</p>
                                    </div>
                                )}

                                <div className="p-3 rounded-lg bg-blue-50 text-blue-700 text-sm flex items-start gap-2">
                                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <p>Após o registo, a sua conta será ativada pelo administrador da empresa.</p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="company">Empresa</Label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                                        <Select
                                            value={companyCode}
                                            onValueChange={setCompanyCode}
                                        >
                                            <SelectTrigger className="pl-10" data-testid="register-company">
                                                <SelectValue placeholder="Selecione a empresa" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {companies.length === 0 ? (
                                                    <SelectItem value="none" disabled>Nenhuma empresa disponível</SelectItem>
                                                ) : (
                                                    companies.map((company) => (
                                                        <SelectItem key={company.id} value={company.code}>
                                                            {company.name}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="name">Nome</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            id="name"
                                            type="text"
                                            placeholder="O seu nome"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="register-name"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="seu@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="register-email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="register-password"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirmar Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            id="confirmPassword"
                                            type="password"
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="register-confirm-password"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                                    disabled={loading || !companyCode || companies.length === 0}
                                    data-testid="register-submit"
                                >
                                    {loading ? 'A criar...' : 'Criar Conta'}
                                </Button>
                            </form>

                            <div className="mt-6 text-center">
                                <p className="text-sm text-slate-500">
                                    Já tem conta?{' '}
                                    <Link
                                        to="/login"
                                        className="font-medium text-emerald-600 hover:text-emerald-700"
                                        data-testid="login-link"
                                    >
                                        Iniciar sessão
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
